
import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Row } from "@tanstack/react-table";
import { Product, Office, Warehouse } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { StatusBadge } from "components/Productlocationstatus";
import { showSuccess, showError } from "toast/toast";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { sendTriggerEmails } from "utils/emailService";
import { fetchUserData, fetchCustomerGroupByDomain, fetchOfficeTeamMembers } from "utils/userDataFetcher";
import { handleApiError } from "utils/errorHandler";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface ExportRow {
  "Office Name": string;
  "Product Name": string;
  "SKU": string;
  "Quantity": number;
  "Brand": string;
  "Category": string;
  "Item Condition": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type Dimensions = {
  length: number;
  height: number;
  width: number;
  weight: number;
};

type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type Image = {
  url: string;
};

type OfficeProduct = {
  product_id: string;
  brokerage_firm: string;
  office: Office;
  product: Product;
  inventory_type: string;
  created_by: { id: string; name: string };
  owner_node_type?: string;
  owner_node_id?: string;
  agent_id?: string;
  agent_email?: string;
};

type SelectionState = {
  [productId: string]: string;
};

const columnHelper = createColumnHelper<OfficeProduct>();
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

const SignageOfficeInventory = () => {
  const [filteredProducts, setFilteredProducts] = useState<OfficeProduct[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const membershipId = getAuthCookie("membershipId");
  const organizationId = getAuthCookie("organizationId");
  const effectiveSignageOrgId = resolveSignageSessionOrganizationId() || organizationId || userId || "";
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();
  const [officeProducts, setOfficeProducts] = useState<OfficeProduct[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const navigate = useNavigate();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const { canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.office_inventory
  );
  
  const [noteSelections, setNoteSelections] = useState<SelectionState>({});
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    ownerId: string;
    productId: string;
    name: string;
  } | null>(null);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [linkedStatusFilter, setLinkedStatusFilter] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState<{ label: string; value: string }[]>([]);
  const [zoneFilter, setZoneFilter] = useState<{ label: string; value: string }[]>([]);
  const [rackFilter, setRackFilter] = useState<{ label: string; value: string }[]>([]);
  const [binFilter, setBinFilter] = useState<{ label: string; value: string }[]>([]);
  const [zoneOptions, setZoneOptions] = useState<{ label: string; value: string }[]>([]);
  const [rackOptions, setRackOptions] = useState<{ label: string; value: string }[]>([]);
  const [binOptions, setBinOptions] = useState<{ label: string; value: string }[]>([]);
  // For Generate Button
  const [generateClicked, setGenerateClicked] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (filteredProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [filteredProducts]);


  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
    ];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const selectedWarehouseIds = warehouseFilter.map(w => w.value); // This will be your ["id1", "id2", ...]
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/incoming-office-linked-products/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              linked_status: "",
              current_status: "",
              warehouse_id: selectedWarehouseIds,
              zone_id: zoneFilter.map((z) => z.value),
              rack_id: rackFilter.map((r) => r.value),
              bin_id: binFilter.map((b) => b.value),
              filters: {
              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );

        if (!response.ok) {
          // throw new Error(`Error: ${response.statusText}`);
          showError("Access denied. You don’t have view permission for Office Inventory.");
        }

        const result = await response.json();
        console.log("Fetched data:", result);
        // setTempObject(data);
        // Ensure the response contains the expected structure
        if (result && Array.isArray(result?.data)) {
          setOfficeProducts(result?.data);
          setFilteredProducts(result?.data);
          // Office datatable returns unfiltered count in `total` and paginated count in `filtered`
          // (agent datatable puts the paginated count in `total` instead).
          setRowCount(result?.filtered ?? result?.total ?? 0);
          setGenerateClicked(false);
        } else {
          console.error("Unexpected API response structure:", data);
          setOfficeProducts([]);
          // setProducts(data.products);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        // Send error email notification
        await handleApiError(err, {
          module: "inventory",
          action: "fetch_office_products",
          userId: userId || undefined,
          url: `${process.env.REACT_APP_API_URL}/signage/incoming-office-linked-products/datatable`,
          method: "POST",
          additionalInfo: {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            warehouseFilter: warehouseFilter.map(w => w.value),
            zoneFilter: zoneFilter.map(z => z.value),
            rackFilter: rackFilter.map(r => r.value),
            binFilter: binFilter.map(b => b.value),
          }
        }, token);
      } finally {
        setLoading(false);
        setGenerateClicked(false);
      }
    };

    fetchProducts();
  }, [token, userId, pagination, globalFilter, generateClicked]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data?.data ?? data?.warehouses ?? []);
        setWarehouses(list);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        // Send error email notification
        await handleApiError(err, {
          module: "inventory",
          action: "fetch_warehouses",
          userId: userId || undefined,
          url: `${process.env.REACT_APP_API_URL}/warehouses`,
          method: "GET"
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [token]);

  const WAREHOUSE_OPTIONS = (Array.isArray(warehouses) ? warehouses : []).map(w => ({
    value: w._id,
    label: w.label,
  }));

  const locationFilterSelectStyles = {
    container: (b: any) => ({ ...b, width: "100%", minWidth: 200 }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
    }),
    menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
  };

  const toLocationOptions = (items: any[] = []) => {
    const map = new Map<string, { label: string; value: string }>();
    items.forEach((item) => {
      const id = String(item?._id || "").trim();
      if (!id) return;
      map.set(id, {
        value: id,
        label: String(item?.label || item?.name || id),
      });
    });
    return Array.from(map.values());
  };

  async function fetchHierarchy(payload: object) {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch hierarchy");
    return res.json();
  }

  const handleWarehouseFilterChange = async (selected: any) => {
    const next = (selected as { label: string; value: string }[]) || [];
    setWarehouseFilter(next);
    setZoneFilter([]);
    setRackFilter([]);
    setBinFilter([]);
    setRackOptions([]);
    setBinOptions([]);
    if (!next.length) {
      setZoneOptions([]);
      return;
    }
    try {
      const results = await Promise.all(
        next.map((w) => fetchHierarchy({ warehouse_id: w.value }))
      );
      setZoneOptions(
        toLocationOptions(results.flatMap((r) => (Array.isArray(r?.zones) ? r.zones : [])))
      );
    } catch (err) {
      console.error("Failed to fetch zones:", err);
      setZoneOptions([]);
    }
  };

  const handleZoneFilterChange = async (selected: any) => {
    const next = (selected as { label: string; value: string }[]) || [];
    setZoneFilter(next);
    setRackFilter([]);
    setBinFilter([]);
    setBinOptions([]);
    if (!next.length) {
      setRackOptions([]);
      return;
    }
    try {
      const results = await Promise.all(
        next.map((z) => fetchHierarchy({ zone_id: z.value }))
      );
      setRackOptions(
        toLocationOptions(results.flatMap((r) => (Array.isArray(r?.racks) ? r.racks : [])))
      );
    } catch (err) {
      console.error("Failed to fetch racks:", err);
      setRackOptions([]);
    }
  };

  const handleRackFilterChange = async (selected: any) => {
    const next = (selected as { label: string; value: string }[]) || [];
    setRackFilter(next);
    setBinFilter([]);
    if (!next.length) {
      setBinOptions([]);
      return;
    }
    try {
      const results = await Promise.all(
        next.map((r) => fetchHierarchy({ rack_id: r.value }))
      );
      setBinOptions(
        toLocationOptions(results.flatMap((r) => (Array.isArray(r?.bins) ? r.bins : [])))
      );
    } catch (err) {
      console.error("Failed to fetch bins:", err);
      setBinOptions([]);
    }
  };

  const handleClearLocationFilters = () => {
    setWarehouseFilter([]);
    setZoneFilter([]);
    setRackFilter([]);
    setBinFilter([]);
    setZoneOptions([]);
    setRackOptions([]);
    setBinOptions([]);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setGenerateClicked((prev) => !prev);
  };

  const handleNoteChange = (productId: string, value: string) => {
    setNoteSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleDeleteClick = (product: OfficeProduct) => {
    const ownerId = String(product?.office?._id || "").trim();
    const productId = String(product?.product_id || "").trim();
    if (!ownerId || !productId) {
      showError("Missing office id for delete.");
      return;
    }
    setDeleteTarget({
      ownerId,
      productId,
      name: product?.product?.name || "Product",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { ownerId, productId } = deleteTarget;
    setDeleting(productId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/office/${encodeURIComponent(ownerId)}/inventory/${encodeURIComponent(productId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        let message = "Failed to delete the product.";
        try {
          const errBody = await response.json();
          message = String(errBody?.error || errBody?.message || message);
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      showSuccess("Product deleted successfully!");
      setOfficeProducts((prev) => prev.filter((p) => p.product_id !== productId));
      setFilteredProducts((prev) => prev.filter((p) => p.product_id !== productId));
      setDeleteTarget(null);
      setGenerateClicked((prev) => !prev);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to delete the product.");
    } finally {
      setDeleting(null);
    }
  };

  const handleUnlink = async (productId: string, officeId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/approve-unlink`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to UnLink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product Unlinked successfully!");

      // Send notifications to signage team members, userId, warehouse group members, and office team members when product is unlinked
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductUnlinkedSignageContent } = await import("../../services/notificationContent");
        const { fetchOfficeTeamMembers } = await import("../../utils/userDataFetcher");
        const officeProduct = officeProducts.find(
          p => p.product_id === productId && p.office._id === officeId
        );
        const productName = officeProduct?.product?.name || "Product";
        
        // Get office name or brokerage firm name for the requester name
        const requesterName = officeProduct?.office?.officeName || officeProduct?.brokerage_firm || "Office User";
        
        // Get effective signage ID (parent if team member, otherwise userId)
        const effectiveSignageId = effectiveSignageOrgId;
        
        // 1. Send notification to userId (the signage user performing the action)
        if (userId && token) {
          try {
            const currentUserData = await fetchUserData(membershipId || userId, token);
            const userLicenseType = currentUserData?.licenseType || "SIGNAGE_TEAM_MEMBER";
            const userRecipientType = userLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
            const content = getProductUnlinkedSignageContent({
              agentOrTeamMemberName: requesterName,
              productName: productName,
              recipientType: userRecipientType,
            });
            await createNotification(userId, content, token, userRecipientType);
          } catch (userErr) {
            console.error(`Failed to send notification to userId ${userId}:`, userErr);
          }
        }
        
        // 2. Send notifications to all signage team members (excluding userId to avoid duplicate)
        if (effectiveSignageId && token) {
          try {
            const signageTeamResp = await fetch(
              `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (signageTeamResp.ok) {
              const signageTeamData = await signageTeamResp.json();
              const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

              // Send notifications to all signage admin/team members (excluding userId to avoid duplicate)
              for (const member of signageTeamMembers) {
                const memberId = member.id || member._id;
                if (memberId && memberId !== userId && token) {
                  try {
                    const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                    const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                    const content = getProductUnlinkedSignageContent({
                      agentOrTeamMemberName: requesterName,
                      productName: productName,
                      recipientType: recipientType,
                    });
                    await createNotification(memberId, content, token, recipientType);
                  } catch (memberErr) {
                    console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                  }
                }
              }
            }
          } catch (signageErr) {
            console.error("Failed to fetch signage team members for notification:", signageErr);
          }
        }
        
        // 3. Send notifications to warehouse group members
        if (effectiveSignageId && token) {
          try {
            const customerGroup = await fetchCustomerGroupByDomain(organizationId, "warehouse", token);
            if (customerGroup?.allowed_user_ids) {
              for (const user of customerGroup.allowed_user_ids) {
                const warehouseUserId = user?.user_id;
                if (warehouseUserId && warehouseUserId !== userId && token) {
                  try {
                    // Warehouse group members are typically SIGNAGE_TEAM_MEMBER
                    const content = getProductUnlinkedSignageContent({
                      agentOrTeamMemberName: requesterName,
                      productName: productName,
                      recipientType: "team_member",
                    });
                    await createNotification(warehouseUserId, content, token, "team_member");
                  } catch (warehouseErr) {
                    console.error(`Failed to send notification to warehouse group member ${warehouseUserId}:`, warehouseErr);
                  }
                }
              }
            }
          } catch (warehouseErr) {
            console.error("Failed to fetch warehouse group members for notification:", warehouseErr);
          }
        }
        
        // 4. Send notifications to office team members
        if (officeId && token) {
          try {
            const officeTeamMembers = await fetchOfficeTeamMembers(officeId, token);
            for (const teamMember of officeTeamMembers) {
              if (teamMember?._id && token) {
                try {
                  const memberLicenseType = teamMember.licenseType || "BROKERAGE_TEAM_MEMBER";
                  const recipientType = memberLicenseType === "BROKERAGE_FIRM" ? "brokerage_admin" : "brokerage_team_member";
                  const content = getProductUnlinkedSignageContent({
                    agentOrTeamMemberName: requesterName,
                    productName: productName,
                    recipientType: recipientType,
                  });
                  await createNotification(teamMember._id, content, token, recipientType);
                } catch (memberErr) {
                  console.error(`Failed to send notification to office team member ${teamMember._id}:`, memberErr);
                }
              }
            }
          } catch (officeErr) {
            console.error("Failed to fetch office team members for notification:", officeErr);
          }
        }
      } catch (notifError) {
        console.error("Failed to send product unlinked notifications:", notifError);
      }

      // Send email notifications
      try {
        // Find the product in the state
        const officeProduct: OfficeProduct | undefined = officeProducts.find(
          p => p.product_id === productId && p.office._id === officeId
        );

        if (officeProduct) {
          const productName = officeProduct?.product?.name || "";
          const brokerageFirmName = officeProduct?.brokerage_firm || "";
          const ownerNodeType = (officeProduct as any)?.owner_node_type || "";
          const ownerNodeId = (officeProduct as any)?.owner_node_id || "";
          const supplierName = userId ? await fetchUserData(membershipId || userId, token).then(u => u?.firstName || u?.email?.split("@")[0] || "Supplier") : "Supplier";
          const reason = noteSelections[productId] || "";
          const unlinkedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

          // Collect recipient emails
          const recipientEmails: string[] = [];
          const licenseTypes: string[] = [];

          // Get customer group users by domain "warehouse"
          const effectiveSignageId = effectiveSignageOrgId;
          const customerGroup = await fetchCustomerGroupByDomain(organizationId, "warehouse", token);
          if (customerGroup?.allowed_user_ids) {
            customerGroup.allowed_user_ids.forEach((user: any) => {
              if (user?.email && user.email.trim()) {
                const email = user.email.trim();
                if (!recipientEmails.includes(email)) {
                  recipientEmails.push(email);
                  licenseTypes.push("SIGNAGE_TEAM_MEMBER");
                }
              }
            });
          }

          // If owner_node_type is "brokerage", fetch and add brokerage email
          // if (ownerNodeType?.toLowerCase() === "brokerage" && ownerNodeId) {
          //   const brokerageUser = await fetchUserData(ownerNodeId, token);
          //   if (brokerageUser?.email) {
          //     const email = brokerageUser.email.trim();
          //     if (!recipientEmails.includes(email)) {
          //       recipientEmails.push(email);
          //       licenseTypes.push("BROKERAGE_FIRM");
          //     }
          //   }
          // }

          // Send emails if there are recipients
          if (recipientEmails.length > 0) {
            const from = process.env.REACT_APP_FROM_EMAIL2 || "";
            const emailParams: Record<string, string> = {
              productName: productName,
              supplierName: supplierName,
              brokerageFirmName: brokerageFirmName,
              unlinkedOn: unlinkedOn,
              reason: reason,
              firstName: "", // Will be replaced in email template
            };

            await sendTriggerEmails(
              "Product Unlinked",
              licenseTypes,
              emailParams,
              recipientEmails,
              from,
              token || ""
            );
            console.log("Email notifications sent for Product Unlinked");
          }
        }
      } catch (emailError) {
        console.error("Failed to send email notifications:", emailError);
        // Don't fail the entire operation if email fails
      }

      navigate(0);
    } catch (error) {
      console.error("Error Unlink product link:", error);
      // Send error email notification
      await handleApiError(error, {
        module: "inventory",
        action: "unlink_office_product",
        userId: userId || undefined,
        url: `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/approve-unlink`,
        method: "PUT",
        additionalInfo: {
          productId,
          officeId
        }
      }, token);
    }
  };
  const columns = useMemo(() => {
    return [
      // columnHelper.display({
      //   id: "serial",
      //   header: () => (
      //     <div className="th-text S-No">S.No.</div>
      //   ),
      //   cell: (info) => (
      //     <div className="text-sm S-No">
      //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
      //     </div>
      //   ),
      //   enableGlobalFilter: false,
      // }),
      columnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text S-No">Image</div>
        ),
        cell: (info) => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (imageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          return (
            <div className="text-sm S-No">
              {url ? (
                <img
                  src={url}
                  alt="Product"
                  style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 4 }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                  }}
                />
              ) : (
                <img
                  src={imageCustomSquare}
                  alt="No image"
                  style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
                />
              )}
            </div>
          );

        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor(
        (row: OfficeProduct) => {
          console.log("brokerage_firm", row?.brokerage_firm);
          return row?.brokerage_firm ?? "";
        },
        {
          id: "Brokerage Name",
          header: () => <div className="th-text">Brokerage Name</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => (
            <span className="text-sm">
              {info.getValue() || "-"}
            </span>
          ),
        }
      ),

      // columnHelper.accessor(
      //   (row: OfficeProduct) => `${row?.brokerage_firm}`,
      //   {
      //     id: "Brokerage Name",
      //     header: () => <div className="th-text">Brokerage Name</div>,
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //     cell: info => <span className="text-sm">{"-"}</span>,
      //   }),
      columnHelper.accessor(
        (row: OfficeProduct) => `${row?.office?.officeName}`,
        {
          id: "office",
          header: () => <div className="th-text">Office</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: info => <span className="table-cells text-center">{info.getValue()}</span>,
        }),
      columnHelper.accessor(
        (row: OfficeProduct) => row?.product?.name,
        {
          id: "name",
          header: () => (
            <div className="th-text">Product Name</div>
          ),
          cell: (info) => {
            const row = info.row.original;  // get full row data
            const productName = row?.product?.name || "N/A"; // fallback if undefined
            return <div className="text-sm wrap-anywhere
">{productName}</div>;
          },
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: OfficeProduct) => row.product.sku || "-",
        {
          id: "sku",
          header: () => (
            <div className="th-text">SKU</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: OfficeProduct) => row.product.category || "-",
        {
          id: "category",
          header: () => (
            <div className="th-text">Category</div>
          ),
          cell: (info) => (
            <div className="text-sm text-ellipsis ">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: OfficeProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
        {
          id: "qty",
          header: () => (
            <div className="th-text">Qty</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      // columnHelper.accessor(
      //   (row: OfficeProduct) => row.product.item_condition || "-",
      //   {
      //     id: "condition",
      //     header: () => (
      //       <div className="th-text">Condition</div>
      //     ),
      //     cell: (info) => (
      //       <div className="text-sm">{info.getValue()}</div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),
      // columnHelper.accessor(
      //   (row: OfficeProduct) => row.created_by?.name || "-",
      //   {
      //     id: "name",
      //     header: () => (
      //       <div className="th-text">Linked By</div>
      //     ),
      //     cell: (info) => (
      //       <div className="text-sm">{info.getValue()}</div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),

      columnHelper.accessor(
        (row: OfficeProduct) =>
          row?.product?.product_location_status?.replace(/_/g, " "),
        {
          id: "product_location_status",
          header: () => (
            <div className="th-text">Handover Method</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: OfficeProduct) =>
          row.product.current_status?.replace(/_/g, " ") || "-",
        {
          id: "current_status",
          header: () => (
            <div className="th-text">
              Product location status
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      //   columnHelper.accessor(
      //   (row: OfficeProduct) =>
      //     row.product.current_status?.replace(/_/g, " ") || "-",
      //   {
      //     id: "current_status",
      //     header: () => (
      //       <div className="th-text">
      //         Product location status
      //       </div>
      //     ),
      //     cell: info => (
      //       <div className="text-sm">
      //         {info.getValue()}
      //       </div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),
      columnHelper.accessor(
        (row: OfficeProduct) =>
          row?.product?.locked_status === "unlocked" ? "" : "Linked",
        {
          id: "locked_status",
          header: () => (
            <div className="th-text ">Linked Status</div>
          ),
          cell: (info) => (
            <div className="text-sm linked-status">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.display({
        id: "SupplierActions",
        header: () => (
          <div className="th-text">
            Supplier Actions
          </div>
        ),
        cell: info => {
          const p = info.row.original;
          const product_id = p.product_id
          const officeId = p.office._id
          return (
            <div className="text-sm">
              {p.product?.linked_mode === "unlock_pending" ? (
                <>
                  <p className="text-danger mb-1 fw-bold">Unlink Requested</p>
                  <button
                    className="btn btn-danger btn-sm mb-2"
                    onClick={() => handleUnlink(p.product_id, officeId)}
                  >
                    Approve
                  </button>
                  <textarea
                    className="form-control"
                    placeholder="Add Reason..."
                    value={noteSelections[product_id] ?? ""}
                    onChange={e => handleNoteChange(product_id, e.target.value)}
                  />
                </>
              ) : (
                "---"
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text">Actions</div>
        ),
        cell: (info) => {
          const productInfo = info.row.original;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="d-flex align-items-center gap-2">
              {canView && (
                <ViewIconButton
                  to={`/signage/editOfficeProduct/${productInfo?.office._id}/${productInfo?.product_id}/brokerage/${officeProducts?.find(p => p.product_id === productInfo.product_id)?.owner_node_id || ""}`}
                />
              )}
              {canEdit && (
                <EditIconButton
                                  to={`${baseRoute}/editOfficeProduct/${productInfo?.office._id}/${productInfo?.product_id}/brokerage/${officeProducts?.find(p => p.product_id === productInfo.product_id)?.owner_node_id || ""}`}

                  className="icon-square" title="Edit"
                />
              )}
              {canDelete && (
                <DeleteIconButton
                  title="Delete"
                  className="icon-square danger"
                  onClick={() => handleDeleteClick(productInfo)}
                  disabled={deleting === productInfo.product_id}
                />
              )}
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination.pageIndex, pagination.pageSize,imageUrlMap, canView, canEdit, canDelete, baseRoute, officeProducts]);


  useEffect(() => {
    const rows: ExportRow[] = filteredProducts.map(product => ({
      "Office Name": product?.office?.officeName || '',
      "Product Name": product?.product?.name || '',
      "SKU": product?.product?.sku || '',
      "Quantity": product?.product?.variants?.[0]?.inventory?.[0]?.available || 0,
      "Brand": product?.product?.brand || '',
      "Category": product?.product?.category || '',
      "Item Condition": product?.product?.item_condition || '',
      "Status": product?.product?.status ? 'Active' : 'Inactive',
    }));

    setExportRows(rows);
  }, [filteredProducts]);

  const table = useReactTable({
    data: officeProducts,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content">

      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Office Inventory</h4>
            {/* <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control search-input-truncate"
                placeholder="Search by  Product Name or SKU Brokerage,Office"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // ✅ Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <ExportAsExcel
              data={exportRows}
              headers={[
                "Office Name",
                "Product Name",
                "SKU",
                "Quantity",
                "Brand",
                "Category",
                "Item Condition",
                "Status"
              ]}
              fileName="OfficeInventory"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>

        <div
          id="incoming-filters"
          className={`po-filters ${filtersOpen ? "show" : ""}`}
          style={filtersOpen ? { maxHeight: 520 } : undefined}
        >
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">

              {/* Warehouses (multi) */}
              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1" htmlFor="warehouseFilter">Warehouses</label>
                <Select
                  inputId="warehouseFilter"
                  isMulti
                  options={WAREHOUSE_OPTIONS}
                  value={warehouseFilter}
                  onChange={handleWarehouseFilterChange}
                  placeholder="Select Warehouses"
                  isClearable
                  classNamePrefix="react-select br-8"
                  styles={locationFilterSelectStyles}
                />
              </div>

              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1" htmlFor="zoneFilter">Zone</label>
                <Select
                  inputId="zoneFilter"
                  isMulti
                  options={zoneOptions}
                  value={zoneFilter}
                  onChange={handleZoneFilterChange}
                  placeholder="Select Zone"
                  isClearable
                  isDisabled={warehouseFilter.length === 0}
                  classNamePrefix="react-select br-8"
                  styles={locationFilterSelectStyles}
                />
              </div>

              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1" htmlFor="rackFilter">Rack</label>
                <Select
                  inputId="rackFilter"
                  isMulti
                  options={rackOptions}
                  value={rackFilter}
                  onChange={handleRackFilterChange}
                  placeholder="Select Rack"
                  isClearable
                  isDisabled={zoneFilter.length === 0}
                  classNamePrefix="react-select br-8"
                  styles={locationFilterSelectStyles}
                />
              </div>

              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1" htmlFor="binFilter">Bin</label>
                <Select
                  inputId="binFilter"
                  isMulti
                  options={binOptions}
                  value={binFilter}
                  onChange={(selected) => setBinFilter((selected as any) || [])}
                  placeholder="Select Bin"
                  isClearable
                  isDisabled={rackFilter.length === 0}
                  classNamePrefix="react-select br-8"
                  styles={locationFilterSelectStyles}
                />
              </div>

              {/* Actions */}
              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button
                  className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                  onClick={() => setGenerateClicked(!generateClicked)}
                >
                  <i className="ri-check-line me-1" />
                  Apply Filter
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-md-grow-0"
                  onClick={handleClearLocationFilters}
                >
                  Clear
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i
              className="bi bi-box-seam me-2"
              aria-hidden="true"
              style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
            />
            <h5 className="mb-0">Office Products</h5>
          </div>

          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />


        {/* DESKTOP/TABLET */}
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading office inventory…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading office inventory…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} totalItems={rowCount} />
        </div>
      </DataTableStatus>
      <style>{`
        .search-input-truncate::placeholder {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Product"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default SignageOfficeInventory;
